import { AlertOutlined, CheckOutlined, SyncOutlined } from '@ant-design/icons';
import { Access, BundleAccess, BundleOwner, User } from '@prisma/client';
import { Button, Descriptions, Result, Space, SpaceProps, Spin, Switch, Tag, Typography } from 'antd';
import Table, { ColumnsType, ColumnType, TableProps } from 'antd/es/table';
import { uniq } from 'lodash';

import { isAgsEntitlementAccess, isAgsRoleAccess, isIemAccess } from '@app/domain-access-interfaces';
import { Role } from '@app/domain-interfaces';
import { formatIsoDate } from '@app/utils-agnostic';
import { Auth, FeatureFlags, Tables, TagList, Theme } from '@app/web-components';

import { AgsAccessNameRenderer, EmployeeTypeRenderer, IemAccessNameRenderer } from '../../common';
import { ApproverTable } from '../../subscriptions/modals';
import { useFindBundleById, useGetEligibilityCounts, usePatchBundle } from '../data';
import { SubscriptionAccessPopover } from '../manage';
import { ProductFamilyHierarchyPopover } from '../modals';

export const useEligibilityColumn = (bundleId: number | null | undefined) => {
  const { data: eligibilityCounts, refetch } = useGetEligibilityCounts({ bundleId });
  const { token } = Theme.useToken();

  const addColumn = (columns: ColumnsType<BundleAccess & { access: Access }> = [], disablePopover = false) => {
    if (eligibilityCounts) {
      columns.splice(1, 0, getColumn(disablePopover));
    }
  };

  const getColumn = (disablePopover = false): ColumnType<BundleAccess & { access: Access }> => ({
    title: 'Eligibility',
    dataIndex: 'eligibleCount',
    width: 120,
    render: (_, record) => {
      const found = eligibilityCounts?.find((x) => x.bundleAccessId === record.id);
      if (!found || !bundleId) return undefined;

      return (
        <SubscriptionAccessPopover bundleId={bundleId} bundleAccessId={record.id} disabled={disablePopover}>
          <Space size={[0, 8]}>
            {found.eligibleCounts.true ? (
              <Tag
                color={token['bright-success']}
                title="Count of users who have access and meet the eligibility validations."
                icon={<CheckOutlined title="Yes" />}
              >
                {found.eligibleCounts.true}
              </Tag>
            ) : undefined}
            {found.eligibleCounts.false ? (
              <Tag
                color={token['bright-error']}
                title="Count of users who have access and DO NOT meet the eligibility validations."
                icon={<AlertOutlined title="No" />}
              >
                {found.eligibleCounts.false}
              </Tag>
            ) : undefined}
            {found.eligibleCounts.null ? (
              <Tag
                color={token['bright-warning']}
                title="Count of users who have access and the eligibility validations have not be completed."
              >
                {found.eligibleCounts.null}
              </Tag>
            ) : undefined}
          </Space>
        </SubscriptionAccessPopover>
      );
    },
  });

  return { addColumn, getColumn, refetch };
};

export const useBundleViewPanel = (bundleId: number | null | undefined, edit = false) => {
  const { data: bundle, isPending, error, refetch } = useFindBundleById({ bundleId });
  const { user } = Auth.useUserAuth();
  const patch = usePatchBundle(bundleId);
  const { isFeatureEnabled, isPending: isFeatureFlagLoading } = FeatureFlags.useFeatureFlags();

  const isTogglesEnabled = isFeatureEnabled('planning_state_exempt');

  const getAccessTableProps = (
    props?: TableProps<BundleAccess & { access: Access }>,
  ): TableProps<BundleAccess & { access: Access }> => {
    const columns: ColumnsType<BundleAccess & { access: Access }> = [
      {
        title: 'Name',
        dataIndex: ['access', 'name'],
        key: 'name',
        render: (name, record) => {
          if (isAgsRoleAccess(record.access) || isAgsEntitlementAccess(record.access)) {
            return <AgsAccessNameRenderer agsAccess={record.access} />;
          } else if (isIemAccess(record.access)) {
            return <IemAccessNameRenderer iemAccess={record.access} />;
          } else {
            return name;
          }
        },
      },
      {
        title: 'Source',
        dataIndex: ['access', 'accessSource'],
        width: 120,
      },
      {
        title: 'Unix Groups',
        dataIndex: 'unixGroups',
        render: (_, record) => {
          const access = record.access;
          if (isAgsEntitlementAccess(access) || isIemAccess(access)) {
            return <TagList.TagList names={access.meta?.unixGroups} showCopyButton />;
          } else if (isAgsRoleAccess(access)) {
            const mergedUnixGroups = access.meta?.entitlements?.flatMap((x) => x.unixGroups ?? []);
            const uniqueUnixGroups = uniq(mergedUnixGroups) ?? [];
            return <TagList.TagList names={uniqueUnixGroups} showCopyButton />;
          }
        },
      },
      {
        title: 'Emp Type',
        dataIndex: 'employeeType',
        width: 120,
        render: (_, record) => <EmployeeTypeRenderer employeeType={record.access.employeeType} />,
      },
    ];

    const unixGroups = bundle
      ? uniq(
          bundle.accesses.flatMap(({ access }) => {
            if (isAgsRoleAccess(access)) {
              const mergedUnixGroups = access.meta?.entitlements?.flatMap((x) => x.unixGroups ?? []);
              return uniq(mergedUnixGroups) ?? [];
            }
            if (isAgsEntitlementAccess(access) || isIemAccess(access)) {
              return access.meta?.unixGroups ?? [];
            }
            return [];
          }),
        ).sort()
      : [];

    return {
      ...props,
      expandable: {
        expandedRowRender: (record) => (
          <Descriptions
            bordered
            size="small"
            layout="horizontal"
            labelStyle={{ backgroundColor: 'inherit' }}
            contentStyle={{ backgroundColor: 'white' }}
            column={2}
          >
            <Descriptions.Item label="Approvers" span={2}>
              <ApproverTable
                accessSource={record.access.accessSource}
                externalId={record.access.externalId}
                name={record.access.name}
                style={{ marginLeft: -40 }}
              />
            </Descriptions.Item>
          </Descriptions>
        ),
      },
      caption: (
        <Typography.Title level={5} style={{ float: 'left' }}>
          Accesses
        </Typography.Title>
      ),
      columns: [...columns, ...(props?.columns ?? [])],
      dataSource: bundle?.accesses ?? [],
      footer:
        unixGroups.length > 0
          ? () => (
              <Button
                style={{ fontSize: '0.75rem', padding: 0 }}
                type="link"
                onClick={() => navigator.clipboard?.writeText(unixGroups.join(', '))}
              >
                Copy All Unix Groups
              </Button>
            )
          : undefined,
      pagination: false,
      rowKey: 'id',
      size: 'small',
    };
  };

  const getOwnersTableProps = (
    props?: TableProps<BundleOwner & { owner: User }>,
  ): TableProps<BundleOwner & { owner: User }> => {
    const columns = [
      {
        title: 'Name',
        dataIndex: ['owner', 'name'],
      },
      {
        title: 'Enterprise Id',
        dataIndex: ['owner', 'enterpriseId'],
        width: 120,
      },
    ];

    return {
      ...props,
      caption: (
        <Typography.Title level={5} style={{ float: 'left' }}>
          Owners
        </Typography.Title>
      ),
      columns,
      dataSource: bundle?.owners ?? [],
      pagination: false,
      rowKey: 'id',
      size: 'small',
    };
  };

  const getPanelSpaceProps = (props?: SpaceProps): SpaceProps => ({
    ...props,
    children: error ? (
      error.response?.status === 404 ? (
        <Result status="404" title="This bundle does not exist or was deleted." />
      ) : (
        <Result status="error" title="Something went wrong." />
      )
    ) : isPending || isFeatureFlagLoading || !bundle ? (
      <Spin style={{ width: '100%' }} />
    ) : (
      <>
        <Descriptions
          bordered
          column={2}
          items={[
            {
              key: 'name',
              label: 'Name',
              children: bundle.name,
            },
            {
              key: 'enable-subscriptions',
              label: 'Pull (eager) Subscriptions Enabled',
              children: (
                <Space direction="horizontal">
                  {edit && isTogglesEnabled ? (
                    <Switch
                      size="small"
                      checkedChildren="ON"
                      disabled={bundle.pullSubscriptionRunning}
                      unCheckedChildren="OFF"
                      value={bundle.pullSubscriptionEnabled}
                      onClick={() => patch.mutate({ pullSubscriptionEnabled: !bundle.pullSubscriptionEnabled })}
                    />
                  ) : (
                    <Tables.BooleanRenderer value={bundle.pullSubscriptionEnabled} />
                  )}
                  {bundle.pullSubscriptionRunning ? (
                    <Typography.Text type="warning">
                      <Space>
                        <SyncOutlined spin />
                        Running
                      </Space>
                    </Typography.Text>
                  ) : undefined}
                </Space>
              ),
            },
            {
              key: 'employee-type',
              label: 'Employee Type',
              children: <EmployeeTypeRenderer employeeType={bundle?.employeeType} />,
            },
            {
              key: 'notifications-enabled',
              label: 'Removal Notifications Enabled',
              children:
                edit && isTogglesEnabled ? (
                  <Switch
                    size="small"
                    checkedChildren="ON"
                    unCheckedChildren="OFF"
                    value={bundle.autoRemovalEnabled}
                    onClick={() => patch.mutate({ autoRemovalEnabled: !bundle.autoRemovalEnabled })}
                  />
                ) : (
                  <Tables.BooleanRenderer value={bundle.autoRemovalEnabled} />
                ),
            },
            bundle.productFamily
              ? {
                  key: 'product-family',
                  label: 'Product Family',
                  children:
                    !user?.roles[Role.BUNDLE_ADMIN] && !user?.roles[Role.RULE_ADMIN] ? (
                      <ProductFamilyHierarchyPopover
                        productFamilyId={bundle.productFamily.id}
                        productFamilyName={bundle.productFamily.name}
                      >
                        <Button type="link" size="small" style={{ padding: 0 }}>
                          {bundle.productFamily.name}
                        </Button>
                      </ProductFamilyHierarchyPopover>
                    ) : (
                      bundle.productFamily.name
                    ),
                }
              : { key: 'product-family', children: undefined },
            {
              key: 'justification-required',
              label: 'Justification Required',
              children:
                edit && isTogglesEnabled ? (
                  <Switch
                    size="small"
                    checkedChildren="ON"
                    unCheckedChildren="OFF"
                    value={bundle.justificationRequired}
                    onClick={() => patch.mutate({ justificationRequired: !bundle.justificationRequired })}
                  />
                ) : (
                  <Tables.BooleanRenderer value={bundle.justificationRequired} />
                ),
            },
            {
              key: 'active',
              label: 'Active',
              children:
                edit && isTogglesEnabled ? (
                  <Switch
                    size="small"
                    checkedChildren="ON"
                    unCheckedChildren="OFF"
                    value={bundle.isActive}
                    onClick={() => patch.mutate({ isActive: !bundle.isActive })}
                  />
                ) : (
                  <Tables.BooleanRenderer value={bundle.isActive} />
                ),
            },
            isTogglesEnabled
              ? {
                  key: 'planning-state-validation',
                  label: 'Planning State Validation Enabled',
                  children: edit ? (
                    <Switch
                      size="small"
                      checkedChildren="ON"
                      unCheckedChildren="OFF"
                      value={bundle.planningStateValidationEnabled}
                      onClick={() =>
                        patch.mutate({ planningStateValidationEnabled: !bundle.planningStateValidationEnabled })
                      }
                    />
                  ) : (
                    <Tables.BooleanRenderer value={bundle.planningStateValidationEnabled} />
                  ),
                }
              : { key: 'empty', children: undefined },
          ]}
          layout="horizontal"
          size="small"
        />
        <>
          <Table {...getOwnersTableProps()} />
          {props?.children}
        </>

        <Descriptions
          bordered
          column={2}
          items={[
            {
              key: 'updated-by',
              label: 'Updated By',
              children: bundle.updatedByUser.name,
            },
            {
              key: 'updated-on',
              label: 'Updated On',
              children: formatIsoDate(bundle.updatedOn),
            },
            {
              key: 'created-by',
              label: 'Created By',
              children: bundle.createdByUser.name,
            },
            {
              key: 'created-on',
              label: 'Created On',
              children: formatIsoDate(bundle.updatedOn),
            },
          ]}
          layout="horizontal"
          size="small"
        />
      </>
    ),
    direction: 'vertical',
    size: 'large',
    style: { ...props?.style, width: '100%' },
  });

  return {
    getAccessTableProps,
    getOwnersTableProps,
    getPanelSpaceProps,
    refetch,
  };
};
